import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Award, Coins, Plus } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ApiError, api, type Paginated, type UserRef } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { formatDateTime, formatMoney, formatMonth, formatNumber } from '../lib/format';
import { newIdempotencyKey } from '../lib/wallet';
import { FormPage } from '../components/FormPage';
import {
  Badge,
  type BadgeTone,
  Button,
  Card,
  Checkbox,
  Detail,
  EmptyState,
  ErrorState,
  Field,
  Input,
  LoadingBlock,
  PageHeader,
  Pagination,
  Select,
  Table,
  Tabs,
  Td,
  Textarea,
  Th,
  Tr,
  useToast,
} from '../components/ui';

type RewardStatus = 'GRANTED' | 'PAID' | 'CANCELLED';

interface Person {
  id: string;
  fullName: string;
  fullNameAr: string | null;
  employeeCode?: string | null;
}

interface DayReward {
  id: string;
  user: Person;
  days: number;
  status: RewardStatus;
  effectiveMonth: string;
  /** Only for whoever may see salaries — an estimate until payroll prices it. */
  amount?: number;
  currency?: string;
  note: string | null;
  grantedBy: Person | null;
  createdAt: string;
  payroll: { periodId: string; year: number; month: number; status: string } | null;
  paidAt: string | null;
  cancelledAt: string | null;
  cancelledBy: Person | null;
  cancellationReason: string | null;
  can: { cancel: boolean };
}

interface PointsGrant {
  id: string;
  user: Person;
  points: number;
  balanceAfter: number;
  reason: string | null;
  grantedBy: Person | null;
  createdAt: string;
}

interface RewardPreview {
  employee: Person;
  grantable: boolean;
  reasons: Array<'SELF' | 'INACTIVE'>;
  salaryVisible: boolean;
  salary: { dailyRate: number | null; currency: string; quarter: number; half: number } | null;
}

export const REWARD_TONE: Record<RewardStatus, BadgeTone> = { GRANTED: 'info', PAID: 'success', CANCELLED: 'neutral' };

/** "Quarter day" / "Half day" — the two sizes a reward comes in (CR-004 §4). */
export function rewardSizeKey(days: number): TranslationKey {
  return days === 0.25 ? 'rewards.size.quarter' : 'rewards.size.half';
}

function monthOf(effectiveMonth: string, locale: 'en' | 'ar') {
  const [year, month] = effectiveMonth.split('-').map(Number);
  return formatMonth(year, month, locale);
}

/**
 * Route: `/rewards` — Rewards & points, for administrators (CR-004): the quarter-
 * and half-day rewards granted (`rewards.manage`), and the points added to wallets
 * by hand with their reasons (`wallet.points.grant`, or the audit trail's readers).
 * The tab lives in the URL (`?tab=points`).
 */
export function RewardsPage() {
  const { t } = useI18n();
  const { can, canAny } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tabs = [
    ...(can(P.REWARDS_MANAGE) ? [{ id: 'rewards', label: t('rewards.tabRewards') }] : []),
    ...(canAny(P.WALLET_POINTS_GRANT, P.AUDIT_VIEW) ? [{ id: 'points', label: t('rewards.tabPoints') }] : []),
  ];
  const requested = params.get('tab');
  const tab = tabs.some((x) => x.id === requested) ? requested! : (tabs[0]?.id ?? 'rewards');

  return (
    <>
      <PageHeader
        title={t('rewards.title')}
        action={
          tab === 'rewards' && can(P.REWARDS_MANAGE) ? (
            <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/rewards/new')}>
              {t('rewards.grant')}
            </Button>
          ) : tab === 'points' && can(P.WALLET_POINTS_GRANT) ? (
            <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/rewards/points/new')}>
              {t('rewards.addPoints')}
            </Button>
          ) : null
        }
      />
      {tabs.length > 1 && (
        <Tabs active={tab} onChange={(id) => setParams(id === tabs[0].id ? {} : { tab: id }, { replace: true })} tabs={tabs} />
      )}
      <div className="mt-5">
        {tab === 'rewards' && <RewardList />}
        {tab === 'points' && <PointsList />}
      </div>
    </>
  );
}

function RewardList() {
  const { t, locale, localized } = useI18n();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<RewardStatus | ''>('');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['rewards', 'list', status, page],
    queryFn: () => api.get<Paginated<DayReward>>('/rewards', { page, pageSize: 25, ...(status ? { status } : {}) }),
  });

  return (
    <>
      <Card className="mb-4" padded={false}>
        <div className="grid gap-3 p-4 sm:grid-cols-3">
          <Select
            value={status}
            aria-label={t('common.status')}
            onChange={(e) => {
              setStatus(e.target.value as RewardStatus | '');
              setPage(1);
            }}
          >
            <option value="">
              {t('common.status')} — {t('common.all')}
            </option>
            {(['GRANTED', 'PAID', 'CANCELLED'] as const).map((s) => (
              <option key={s} value={s}>
                {t(`rewards.status.${s}` as TranslationKey)}
              </option>
            ))}
          </Select>
        </div>
      </Card>
      <Card padded={false}>
        {isLoading ? (
          <LoadingBlock />
        ) : error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : !data?.items.length ? (
          <EmptyState icon={<Award className="h-8 w-8" />} title={t('rewards.none')} />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>{t('rewards.employee')}</Th>
                  <Th>{t('rewards.size')}</Th>
                  <Th>{t('common.status')}</Th>
                  <Th>{t('rewards.grantedBy')}</Th>
                  <Th>{t('rewards.note')}</Th>
                  <Th align="end">{t('common.actions')}</Th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((r) => (
                  <Tr key={r.id}>
                    <Td>
                      <span className="block">{localized(r.user)}</span>
                      {r.user.employeeCode && <span className="font-mono text-mono-sm text-faint">{r.user.employeeCode}</span>}
                    </Td>
                    <Td className="whitespace-nowrap">
                      <span className="font-medium">{t(rewardSizeKey(r.days))}</span>
                      {r.amount != null && r.currency && (
                        <span className="block text-caption tabular text-muted">≈ {formatMoney(r.amount, r.currency, locale)}</span>
                      )}
                    </Td>
                    <Td>
                      <Badge tone={REWARD_TONE[r.status]}>{t(`rewards.status.${r.status}` as TranslationKey)}</Badge>
                      <span className="mt-0.5 block text-caption text-muted">
                        {r.status === 'PAID' && r.payroll
                          ? t('rewards.paidWith', { month: formatMonth(r.payroll.year, r.payroll.month, locale) })
                          : r.status === 'GRANTED' && r.payroll
                            ? t('rewards.onPayroll', { month: formatMonth(r.payroll.year, r.payroll.month, locale) })
                            : r.status === 'CANCELLED'
                              ? r.cancellationReason
                              : monthOf(r.effectiveMonth, locale)}
                      </span>
                    </Td>
                    <Td className="whitespace-nowrap">
                      <span className="block">{r.grantedBy ? localized(r.grantedBy) : '—'}</span>
                      <span className="text-caption tabular text-muted">{formatDateTime(r.createdAt, locale)}</span>
                    </Td>
                    <Td className="max-w-xs">
                      <span className="line-clamp-2 text-body-sm text-[var(--text-secondary)]" dir="auto">
                        {r.note ?? '—'}
                      </span>
                    </Td>
                    <Td align="end">
                      {r.can.cancel && (
                        <Button size="sm" variant="ghost" onClick={() => navigate(`/rewards/${r.id}/cancel`)}>
                          {t('rewards.withdraw')}
                        </Button>
                      )}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            <Pagination page={data.page} pageCount={data.pageCount} total={data.total} onChange={setPage} />
          </>
        )}
      </Card>
    </>
  );
}

function PointsList() {
  const { t, locale, localized } = useI18n();
  const [page, setPage] = useState(1);
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['wallet', 'grants', page],
    queryFn: () => api.get<Paginated<PointsGrant>>('/wallet/grants', { page, pageSize: 25 }),
  });

  return (
    <Card padded={false}>
      {isLoading ? (
        <LoadingBlock />
      ) : error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : !data?.items.length ? (
        <EmptyState icon={<Coins className="h-8 w-8" />} title={t('points.none')} />
      ) : (
        <>
          <Table>
            <thead>
              <tr>
                <Th>{t('rewards.employee')}</Th>
                <Th align="end">{t('points.points')}</Th>
                <Th>{t('points.reason')}</Th>
                <Th>{t('points.by')}</Th>
                <Th align="end">{t('points.balanceAfter')}</Th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((g) => (
                <Tr key={g.id}>
                  <Td>
                    <span className="block">{localized(g.user)}</span>
                    {g.user.employeeCode && <span className="font-mono text-mono-sm text-faint">{g.user.employeeCode}</span>}
                  </Td>
                  <Td align="end" className="whitespace-nowrap font-medium tabular text-success-fg dark:text-success-dark">
                    <bdi dir="ltr">+{formatNumber(g.points, locale)}</bdi>
                  </Td>
                  <Td className="max-w-sm">
                    <span className="line-clamp-3 text-body-sm" dir="auto">
                      {g.reason}
                    </span>
                  </Td>
                  <Td className="whitespace-nowrap">
                    <span className="block">{g.grantedBy ? localized(g.grantedBy) : '—'}</span>
                    <span className="text-caption tabular text-muted">{formatDateTime(g.createdAt, locale)}</span>
                  </Td>
                  <Td align="end" className="tabular text-muted">
                    {formatNumber(g.balanceAfter, locale)}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
          <Pagination page={data.page} pageCount={data.pageCount} total={data.total} onChange={setPage} />
        </>
      )}
    </Card>
  );
}

/** Everyone the administrator may pick — never themselves. */
function useEmployeeOptions() {
  const { user } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ['users', 'options'],
    queryFn: () => api.get<UserRef[]>('/users/options'),
  });
  return { people: (data ?? []).filter((p) => p.id !== user?.id), isLoading };
}

/**
 * Route: `/rewards/new` — an administrator grants an employee a reward of a quarter
 * or half of a day's pay, confirmed before it is granted. The money shows only to
 * whoever may see salaries. The employee is told in the bell; a double click or a
 * retry grants once (one request key per visit to the page).
 */
export function RewardFormPage() {
  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { people } = useEmployeeOptions();
  const [userId, setUserId] = useState('');
  const [days, setDays] = useState<0.25 | 0.5 | null>(null);
  const [note, setNote] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [key] = useState(newIdempotencyKey);

  const preview = useQuery({
    queryKey: ['rewards', 'preview', userId],
    queryFn: () => api.get<RewardPreview>('/rewards/preview', { userId }),
    enabled: !!userId,
  });

  const grant = useMutation({
    mutationFn: () =>
      api.post<{ replayed: boolean; reward: DayReward }>('/rewards', {
        userId,
        days,
        ...(note.trim() ? { note: note.trim() } : {}),
        idempotencyKey: key,
      }),
    onSuccess: (result) => {
      toast.success(result.replayed ? t('rewards.alreadyGranted') : t('rewards.grantedToast', { name: localized(result.reward.user) }));
      void queryClient.invalidateQueries({ queryKey: ['rewards'] });
      navigate('/rewards');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const person = people.find((p) => p.id === userId);
  const grantable = !!preview.data?.grantable;
  const salary = preview.data?.salary;

  return (
    <FormPage
      title={t('rewards.grant')}
      backTo="/rewards"
      backLabel={t('rewards.title')}
      submitLabel={t('rewards.grant')}
      onSubmit={() => grant.mutate()}
      submitting={grant.isPending}
      disabled={!userId || days == null || !grantable || !confirmed}
    >
      <div className="space-y-4">
        <Field label={t('rewards.employee')} required>
          <Select
            value={userId}
            onChange={(e) => {
              setUserId(e.target.value);
              setConfirmed(false);
            }}
          >
            <option value="">{t('rewards.pickEmployee')}</option>
            {people.map((p) => (
              <option key={p.id} value={p.id}>
                {localized(p)}
              </option>
            ))}
          </Select>
        </Field>
        {preview.data && !grantable && (
          <p className="text-body-sm text-error-fg dark:text-error-dark">
            {preview.data.reasons.map((r) => t(`rewards.cannot.${r}` as TranslationKey)).join(' ')}
          </p>
        )}

        <fieldset>
          <legend className="mb-1.5 text-body-sm font-medium text-[var(--text-secondary)]">
            {t('rewards.size')} <span className="text-primary dark:text-primary-400">*</span>
          </legend>
          <div className="grid gap-3 sm:grid-cols-2">
            {([0.25, 0.5] as const).map((size) => (
              <label
                key={size}
                className={clsx(
                  'flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors duration-fast',
                  days === size ? 'border-primary bg-primary/5' : 'hairline',
                )}
              >
                <input
                  type="radio"
                  name="size"
                  aria-label={t(rewardSizeKey(size))}
                  className="mt-1 h-4 w-4 accent-primary"
                  checked={days === size}
                  onChange={() => {
                    setDays(size);
                    setConfirmed(false);
                  }}
                />
                <span className="min-w-0">
                  <span className="block text-body font-medium text-[var(--text-primary)]">{t(rewardSizeKey(size))}</span>
                  {salary && (
                    <span className="block text-caption tabular text-muted">
                      {t('rewards.preview', { amount: formatMoney(size === 0.25 ? salary.quarter : salary.half, salary.currency, locale) })}
                    </span>
                  )}
                </span>
              </label>
            ))}
          </div>
          <p className="mt-1.5 text-caption text-faint">
            {t('rewards.sizeHint')} {preview.data && !preview.data.salaryVisible && t('rewards.previewHidden')}
          </p>
        </fieldset>

        <Field label={t('rewards.note')} hint={t('rewards.noteHint')}>
          <Textarea value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} dir="auto" />
        </Field>

        {person && days != null && grantable && (
          <Checkbox
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            label={t('rewards.confirm', { name: localized(person), size: t(rewardSizeKey(days)) })}
          />
        )}
      </div>
    </FormPage>
  );
}

/**
 * Route: `/rewards/:id/cancel` — withdraw a reward no approved payroll carries, with
 * a reason the administrators keep. One on a payroll not approved yet comes off its
 * payslip. The employee is told it was withdrawn, not why.
 */
export function RewardCancelPage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');

  const { data: reward, isLoading, error, refetch } = useQuery({
    queryKey: ['rewards', 'one', id],
    queryFn: () => api.get<DayReward>(`/rewards/${id}`),
  });

  const cancel = useMutation({
    mutationFn: () => api.post<DayReward>(`/rewards/${id}/cancel`, { reason: reason.trim() }),
    onSuccess: () => {
      toast.success(t('rewards.cancelledToast'));
      void queryClient.invalidateQueries({ queryKey: ['rewards'] });
      navigate('/rewards');
    },
    onError: (e: Error) => {
      toast.error(e.message);
      void refetch();
    },
  });

  return (
    <FormPage
      title={t('rewards.cancelTitle')}
      backTo="/rewards"
      backLabel={t('rewards.title')}
      loading={isLoading}
      error={error}
      onRetry={() => void refetch()}
      summary={
        reward && (
          <dl className="divide-y divide-[var(--line)]">
            <Detail label={t('rewards.employee')}>{localized(reward.user)}</Detail>
            <Detail label={t('rewards.size')}>{t(rewardSizeKey(reward.days))}</Detail>
            <Detail label={t('common.status')}>
              <Badge tone={REWARD_TONE[reward.status]}>{t(`rewards.status.${reward.status}` as TranslationKey)}</Badge>
            </Detail>
            <Detail label={t('rewards.month')}>
              {reward.payroll ? formatMonth(reward.payroll.year, reward.payroll.month, locale) : monthOf(reward.effectiveMonth, locale)}
            </Detail>
          </dl>
        )
      }
      submitLabel={t('rewards.withdraw')}
      onSubmit={() => cancel.mutate()}
      submitting={cancel.isPending}
      disabled={!reward?.can.cancel || !reason.trim()}
      destructive
    >
      <Field label={t('rewards.cancelReason')} required hint={t('rewards.cancelHint')}>
        <Textarea value={reason} maxLength={1000} onChange={(e) => setReason(e.target.value)} dir="auto" autoFocus />
      </Field>
    </FormPage>
  );
}

/**
 * Route: `/rewards/points/new` — an administrator adds points to an employee's
 * wallet, with a reason only administrators read: the employee sees how many and
 * when, never why (CR-004 §5). Confirmed first; a retry adds once.
 */
export function PointsGrantPage() {
  const { t, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { people } = useEmployeeOptions();
  const [userId, setUserId] = useState('');
  const [points, setPoints] = useState('');
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [key] = useState(newIdempotencyKey);

  const grant = useMutation({
    mutationFn: () =>
      api.post<{ replayed: boolean; balance: number; points: number }>('/wallet/grants', {
        userId,
        points: Number(points),
        reason: reason.trim(),
        idempotencyKey: key,
      }),
    onSuccess: (result) => {
      const person = people.find((p) => p.id === userId);
      toast.success(
        result.replayed ? t('points.alreadyAdded') : t('points.addedToast', { points: result.points, name: person ? localized(person) : '' }),
      );
      void queryClient.invalidateQueries({ queryKey: ['wallet'] });
      navigate('/rewards?tab=points');
    },
    onError: (e: Error) => toast.error(e instanceof ApiError && e.status === 403 ? t('points.self') : e.message),
  });

  const person = people.find((p) => p.id === userId);
  const pointsOk = /^\d+$/.test(points.trim()) && Number(points) >= 1 && Number(points) <= 1_000_000;
  const reasonOk = reason.trim().length >= 3 && reason.trim().length <= 500;

  return (
    <FormPage
      title={t('points.grantTitle')}
      backTo="/rewards?tab=points"
      backLabel={t('rewards.title')}
      submitLabel={t('rewards.addPoints')}
      onSubmit={() => grant.mutate()}
      submitting={grant.isPending}
      disabled={!userId || !pointsOk || !reasonOk || !confirmed}
    >
      <div className="space-y-4">
        <Field label={t('rewards.employee')} required>
          <Select
            value={userId}
            onChange={(e) => {
              setUserId(e.target.value);
              setConfirmed(false);
            }}
          >
            <option value="">{t('rewards.pickEmployee')}</option>
            {people.map((p) => (
              <option key={p.id} value={p.id}>
                {localized(p)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('points.points')} required hint={t('points.pointsHint')} error={points && !pointsOk ? t('points.pointsHint') : null}>
          <Input
            type="number"
            inputMode="numeric"
            min={1}
            max={1_000_000}
            step={1}
            value={points}
            onChange={(e) => {
              setPoints(e.target.value);
              setConfirmed(false);
            }}
            className="!w-40 tabular"
            dir="ltr"
          />
        </Field>
        <Field label={t('points.reason')} required hint={t('points.reasonHint')}>
          <Textarea value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} dir="auto" />
        </Field>
        {person && pointsOk && (
          <Checkbox
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            label={t('points.confirm', { points: Number(points), name: localized(person) })}
          />
        )}
      </div>
    </FormPage>
  );
}
